import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Share2, Download, Copy, X, Twitter, Linkedin } from "lucide-react";

export default function ShareCard({ open, onClose }) {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  if (!open || !user) return null;

  const backend = process.env.REACT_APP_BACKEND_URL;
  const imgUrl = `${backend}/api/share/streak/${user.id}.png?t=${Date.now()}`;
  const siteUrl = window.location.origin;
  const text = `I'm spotting AI from human content — ${user.points} pts, ${user.streak_days}-day streak. Can you beat me? ${siteUrl}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable */
    }
  };

  const dl = () => {
    const a = document.createElement("a");
    a.href = imgUrl;
    a.download = `aivshuman-${user.name}.png`;
    a.click();
  };

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.85)" }}
      onClick={onClose}
      data-testid="share-modal"
    >
      <div
        className="card max-w-2xl w-full p-6 md:p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 nav-link"
          aria-label="Close"
          data-testid="share-close-button"
        >
          <X size={18} />
        </button>
        <div className="label-mono mb-2" style={{ color: "var(--ai)" }}>Share your streak</div>
        <div className="font-display font-bold text-2xl mb-5">Drop this on LinkedIn, X, or anywhere.</div>

        <div className="media-frame mb-5">
          <img src={imgUrl} alt="Your AI vs Human card" className="w-full" data-testid="share-card-image" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button className="btn btn-primary" onClick={dl} data-testid="share-download">
            <Download size={14} /> PNG
          </button>
          <a
            className="btn btn-secondary"
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`}
            target="_blank" rel="noreferrer"
            data-testid="share-twitter"
          >
            <Twitter size={14} /> X / Twitter
          </a>
          <a
            className="btn btn-secondary"
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(siteUrl)}`}
            target="_blank" rel="noreferrer"
            data-testid="share-linkedin"
          >
            <Linkedin size={14} /> LinkedIn
          </a>
          <button className="btn btn-secondary" onClick={copyLink} data-testid="share-copy">
            <Copy size={14} /> {copied ? "Copied!" : "Copy text"}
          </button>
        </div>

        <div className="label-mono mt-5" style={{ color: "var(--text-dim)" }}>
          Tip: your stats refresh every time you vote. Re-share to flex your latest streak.
        </div>
      </div>
    </div>
  );
}

export function ShareButton({ className = "", testId = "share-button" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className={`btn btn-secondary ${className}`} onClick={() => setOpen(true)} data-testid={testId}>
        <Share2 size={14} /> Share my streak
      </button>
      <ShareCard open={open} onClose={() => setOpen(false)} />
    </>
  );
}
